import { hasConsent, type ConsentCategory } from '@shared/consent';

/**
 * DIT-513 — Consent Mode v2, advanced.
 *
 * Google's tags read a consent state from the dataLayer rather than being
 * blocked by us. Two things have to happen for that to be true: a **denied
 * default pushed before the container loads**, and an **update on every consent
 * change**. Miss the first and the tags run once with no state and assume
 * granted; miss the second and a visitor who accepts is measured as if they had
 * not.
 *
 * *Advanced*, the client's choice of 16 September: the container loads for every
 * visitor, the funnel's events are pushed whatever the answer (`events.ts`), and
 * where consent is denied Google's tags send pings without cookies from which
 * conversions are modelled. What changes before consent is therefore on the list
 * for the legal review of the banner and the privacy policy, and the banner's
 * wording is deliberately not changed ahead of it. Server-side tagging remains its
 * own piece of work.
 */

/** The four signals the ticket names, mapped to the categories our banner asks for. */
const SIGNALS = {
  // Advertising. All three follow the marketing category, because that is the
  // one the banner describes as advertising — splitting them would mean asking
  // a question the banner does not ask.
  ad_storage: 'marketing',
  ad_user_data: 'marketing',
  ad_personalization: 'marketing',
  analytics_storage: 'analytics',
} as const satisfies Record<string, ConsentCategory>;

type Signal = keyof typeof SIGNALS;
type State = 'granted' | 'denied';

/**
 * Deliberately only these four. Consent Mode also defines `functionality_storage`,
 * `personalization_storage` and `security_storage`; the ticket names four and the
 * banner has no category that maps honestly onto the others, so sending a guess
 * for them would be worse than sending nothing.
 */
export const CONSENT_SIGNALS = Object.keys(SIGNALS) as Signal[];

export type DataLayerEntry = unknown[] | Record<string, unknown>;

interface WithDataLayer {
  dataLayer?: DataLayerEntry[];
}

/** The queue GTM reads. Created here if the container has not loaded yet — that
 *  is the whole point of the default state, which is pushed before it exists.
 *  The ONE accessor — events.ts and gtm.ts import it rather than growing their
 *  own copies, so a change to how the queue is created happens once. */
export function dataLayer(): DataLayerEntry[] | null {
  if (typeof window === 'undefined') return null;
  const scope = window as WithDataLayer;
  scope.dataLayer = scope.dataLayer || [];
  return scope.dataLayer;
}

/**
 * `gtag` as Google defines it: a function that pushes its **arguments object**,
 * not an array of its arguments.
 *
 * This looks like a detail and is not. `arguments` serialises with numeric keys
 * and Google's tags read it back that way; pushing `[...args]` produces a
 * structurally similar object that the tags ignore, and the failure is silent —
 * the consent state simply never applies.
 */
const gtag = function () {
  const queue = dataLayer();
  // eslint-disable-next-line prefer-rest-params
  if (queue) queue.push(arguments as unknown as DataLayerEntry);
} as (...args: unknown[]) => void;

function stateOf(category: ConsentCategory): State {
  return hasConsent(category) ? 'granted' : 'denied';
}

function payload(resolve: (category: ConsentCategory) => State): Record<Signal, State> {
  const out = {} as Record<Signal, State>;
  for (const signal of CONSENT_SIGNALS) out[signal] = resolve(SIGNALS[signal]);
  return out;
}

/** Whether this page has pushed its default yet — see `consentModeDefault`. */
let defaultPushed = false;

/**
 * The denied default. Must run before the container script is added to the page.
 *
 * `wait_for_update` holds Google's tags for half a second so a visitor whose
 * choice is already stored — a returning visitor, whose banner never appears —
 * is measured with their real state rather than with the default. Without it
 * there is a race that shows up as under-reported conversions from repeat
 * visitors, which is the hardest kind of gap to notice.
 */
export function consentModeDefault(): void {
  // Once per page. Both the container loader and an early update ask for it, and
  // a second default arriving after an update would put the visitor back to
  // denied.
  if (defaultPushed) return;
  defaultPushed = true;
  gtag('consent', 'default', {
    ...payload(() => 'denied'),
    wait_for_update: 500,
  });
  // ⚠ Advanced mode sends Google Ads' pings for visitors who have not accepted, and
  // without redaction those pings carry the click identifier from the page's
  // address while `ad_storage` is denied. Set here rather than left to the
  // container, so it does not depend on a setting this repository cannot see.
  gtag('set', 'ads_data_redaction', true);
}

/** The dataLayer event pushed after every consent update. */
export const CONSENT_UPDATE_EVENT = 'di_consent_update';

/**
 * The visitor's actual state. Safe to call repeatedly; Google expects it on every change.
 *
 * **Followed by a dataLayer event**, because Consent Mode gates Google's tags and
 * nothing else. A non-Google tag — the Meta pixel is the likely one — has to be
 * held back by the container itself, and a container cannot fire it after a
 * first-time accept without an event to trigger on. `di_consent_update` carries
 * the two category answers, and they stay readable in the data layer afterwards.
 * **Each non-Google tag waits for its own category's key**: an advertising tag (the
 * Meta pixel) for `consent_marketing`, an analytics one (Microsoft Clarity) for
 * `consent_analytics` — gated on the other key, Clarity would record sessions for
 * visitors who accepted Advertising alone and never for those who accepted
 * Analytics. A non-Google conversion tag triggered on `signup_submitted` or
 * `account_created` needs its category's condition on its own trigger, since those
 * events now arrive whatever the answer.
 */
export function consentModeUpdate(): void {
  // An update is only meaningful after a default. The listener is registered
  // before the banner starts, so an answer announced at start-up can arrive
  // before the container loader has pushed one.
  consentModeDefault();
  const state = payload(stateOf);
  gtag('consent', 'update', state);
  dataLayer()?.push({
    event: CONSENT_UPDATE_EVENT,
    consent_analytics: state.analytics_storage,
    consent_marketing: state.ad_storage,
  });
}
